// src/utils/permissions.js

/* ════════════════════════════════════════
   DEPT TAB ACCESS
════════════════════════════════════════ */
export const DEPT_TABS = {
  packhouse:  ["workers", "varieties", "harvest", "lengths", "bunch"],
  production: ["ghse", "field", "varieties"],
};

/* ════════════════════════════════════════
   HELPERS
════════════════════════════════════════ */
export const isAdmin = (user) => {
  if (!user) return false;
  return user.role?.toLowerCase() === "admin" || user.is_superuser;
};

export const getUserDepartment = (user) => {
  if (!user) return null;
  return user.department?.toLowerCase() ?? null;
};

/* ════════════════════════════════════════
   TAB VISIBILITY
════════════════════════════════════════ */
export const canAccessTab = (user, tabKey) => {
  if (!user) return false;
  if (isAdmin(user)) return true;
  if (tabKey === "submissions") return false;

  const rights = user.access_rights;
  const manageRights = {
    packhouse_reports: "manage.reports", buncher_targets: "manage.targets",
    bunching_window: "manage.bunching_window", rejection_reasons: "manage.rejection_reasons",
    workers: "manage.workers",
    varieties: "manage.varieties", harvest: "manage.harvest_types", lengths: "manage.lengths",
    bunch: "manage.bunch_sizes", ghse: "manage.ghse", field: "manage.field_adders",
  };
  if (rights && manageRights[tabKey]) return Boolean(rights[manageRights[tabKey]]);

  const dept = getUserDepartment(user);
  return DEPT_TABS[dept]?.includes(tabKey) ?? false;
};

export const getVisibleTabs = (user, allTabs) => {
  if (!user) return [];
  if (isAdmin(user)) return allTabs;

  const dept = getUserDepartment(user);
  return allTabs.filter((t) => canAccessTab(user, t.key));
};

/* ════════════════════════════════════════
   FEATURE ACCESS
════════════════════════════════════════ */
export const canAccess = (user, feature) => {
  if (!user) return false;

  if (feature === "client_portal" && user.access_rights) {
    return ["clients.portal.view", "clients.manage", "clients.catalog", "clients.orders", "clients.setup", "clients.dispatch_email"]
      .some((right) => Boolean(user.access_rights[right]));
  }

  const featureRights = {
    dashboard: "dashboard.view",
    analytics: "analytics.view",
    intake_analytics: "intake_analytics.view",
    buncher_performance: "buncher_performance.view",
    rejections_analytics: "rejections_analytics.view",
    capture: "capture.view",
    harvest_data_capture: "harvest_data_capture.view",
    intake_qc: "intake_qc.view",
    session: "session.view",
    manage: "manage.view",
    users: "users.view",
    client_portal: "clients.portal.view",
    rejections: "rejections.view",
  };
  if (user.access_rights && Object.hasOwn(featureRights, feature)) {
    return Boolean(user.access_rights[featureRights[feature]]);
  }

  const role = user.role?.toLowerCase();
  const department = user.department?.toLowerCase();

  /* ─────────────────────────────
     ADMIN → FULL ACCESS
  ───────────────────────────── */
  if (role === "admin") return true;

  /* ─────────────────────────────
     ANALYTICS HUB (PAGE + ALL HOOKS)
     Matches:
     - analytics
     - analytics_field
     - analytics_packhouse
     - analytics_intake_qc
     - analytics_rejections
  ───────────────────────────── */
  if (feature === "analytics" || feature.startsWith("analytics_")) {
    return (
      role === "packhouse_manager" ||
      role === "field_manager"
    );
  }

  /* ─────────────────────────────
     QC + PACKHOUSE
  ───────────────────────────── */
  if (role === "qc" && department === "packhouse") {
    return feature === "capture" || feature === "rejections";
  }

  /* ─────────────────────────────
     TEAM LEADER + PRODUCTION
  ───────────────────────────── */
  if (role === "team_leader" && department === "production") {
    return feature === "harvest_data_capture";
  }

  /* ─────────────────────────────
     TEAM LEADER + PACKHOUSE
  ───────────────────────────── */
  if (role === "team_leader" && department === "packhouse") {
    return feature === "intake_qc" || feature === "rejections";
  }

  /* ─────────────────────────────
     FIELD MANAGER + PRODUCTION
  ───────────────────────────── */
  if (role === "field_manager" && department === "production") {
    return (
      feature === "harvest_data_capture" ||
      feature === "manage"
    );
  }

  /* ─────────────────────────────
     PACKHOUSE MANAGER + PACKHOUSE
  ───────────────────────────── */
  if (role === "packhouse_manager" && department === "packhouse") {
    return (
      feature === "capture"   ||
      feature === "intake_qc" ||
      feature === "session"   ||
      feature === "manage"    ||
      feature === "rejections"
    );
  }

  return false;
};
